import { AccessDenied, Check, Question } from '@tunasong/icons'
import type { Availability } from '@tunasong/schemas'
import { availabilityValues } from '@tunasong/schemas'
import type { ChangeEvent, FC, KeyboardEvent } from 'react'
import styles from './availability-control.module.css'

const labels: Record<Availability, string> = { yes: 'Yes', maybe: 'Maybe', no: 'No' }

/**
 * Number keys do not collide with radio semantics — type-ahead is a listbox behaviour, not a
 * radio one — so `1` / `2` / `3` are free to use, and on a ten-slot poll they are what makes
 * answering fast.
 */
const shortcuts: Record<string, Availability> = { '1': 'yes', '2': 'maybe', '3': 'no' }
const glyphs: Record<Availability, FC> = { yes: Check, maybe: Question, no: AccessDenied }

export interface AvailabilityControlProps {
  /** Unique per slot — the shared `name` that makes the three radios one choice. */
  name: string
  /** Announced as the group's legend. The full date, e.g. `Tuesday 24 March 2026, 19:00–21:00`. */
  legend: string
  value?: Availability
  disabled?: boolean
  onChange(value: Availability): void
}

/**
 * Exclusive three-state availability control for one slot.
 *
 * Deliberately NOT `ToggleButtonGroup`: that primitive emits `role="group"` with `aria-pressed`
 * on each button, which announces three independent toggles rather than one choice — a
 * screen-reader user gets no signal that picking Maybe unpicks Yes. It is shared and its other
 * callers want those semantics, so this control is built locally.
 *
 * Equally not ui-lib's `RadioGroup`, which renders its own `<div role="radiogroup">`; nesting
 * that inside a `fieldset` announces the group twice. A native fieldset with bare radios gives
 * the same semantics once.
 */
export const AvailabilityControl: FC<AvailabilityControlProps> = props => {
  const { name, legend, value, disabled, onChange } = props

  const handleChange = (next: Availability) => (ev: ChangeEvent<HTMLInputElement>) => {
    if (ev.target.checked) {
      onChange(next)
    }
  }

  const handleKeyDown = (ev: KeyboardEvent<HTMLFieldSetElement>) => {
    /** A modified press belongs to the browser or the OS. */
    if (disabled || ev.metaKey || ev.ctrlKey || ev.altKey) {
      return
    }
    const next = shortcuts[ev.key]
    if (!next) {
      return
    }
    ev.preventDefault()
    onChange(next)
  }

  return (
    <fieldset className={styles.fieldset} disabled={disabled} onKeyDown={handleKeyDown}>
      <legend className={styles.legend}>{legend}</legend>
      {availabilityValues.map(state => {
        const Glyph = glyphs[state]
        return (
          <label key={state} className={styles[state]}>
            <input
              type="radio"
              className={styles.input}
              name={name}
              value={state}
              checked={value === state}
              disabled={disabled}
              onChange={handleChange(state)}
            />
            {/* State is never colour alone (WCAG 1.4.1): each state carries its own glyph. */}
            <span className={styles.glyph} data-availability-glyph={state} aria-hidden="true">
              <Glyph />
            </span>
            <span className={styles.stateLabel}>{labels[state]}</span>
          </label>
        )
      })}
    </fieldset>
  )
}

export default AvailabilityControl
